/** @jsxRuntime classic */
/** @jsx h */

import {
  type Component,
  computed,
  h,
  type InjectionKey,
  inject,
  provide,
  type VNode,
} from 'vue'
import * as parse from './parse.ts'
import {
  type MarkdownToJSX,
  type RequireAtLeastOne,
  RuleType,
} from './types.ts'
import * as util from './utils.ts'

export { parser } from './parse.ts'

export { type MarkdownToJSX, RuleType } from './types.ts'
export { sanitizer, slugify } from './utils.ts'

/**
 * Vue injection key for sharing compiler options across Markdown components
 */
export const MarkdownOptionsKey: InjectionKey<VueOptions | undefined> =
  Symbol('markdown-options')

// Shared type for Vue child elements
type VueChild = VNode | string

// Vue sanitizer type (uses string instead of React's HTMLTags)
type VueSanitizer = (
  value: string,
  tag: string,
  attribute: string
) => string | null

type VueAstRender = (
  ast: MarkdownToJSX.ASTNode | MarkdownToJSX.ASTNode[],
  state: MarkdownToJSX.State
) => VueChild[] | VueChild | null

// Helper to normalize children output to array format (only for null handling)
// Inlined in hot paths for performance
const normalizeChildren = (output: VueChild[] | VueChild | null): VueChild[] =>
  Array.isArray(output) ? output : output === null ? [] : [output]

// Render functions for each node type
const renderers: Record<
  number,
  (node: any, ctx: RenderContext) => VueChild[] | VueChild | null
> = {
  [RuleType.blockQuote]: (node, { h, output, state, slug }) => {
    const props = {} as Record<string, unknown>
    let children = node.children
    if (node.alert) {
      props.class = `markdown-alert-${slug(node.alert.toLowerCase(), util.slugify)}`
      children = [util.alertHeaderNode(node.alert), ...children]
    }
    return h(
      'blockquote',
      { key: state.key, ...props },
      ...normalizeChildren(output(children, state))
    )
  },

  [RuleType.breakLine]: (_, { h, state }) => h('br', { key: state.key }),

  [RuleType.breakThematic]: (_, { h, state }) => h('hr', { key: state.key }),

  [RuleType.frontmatter]: (node, { h, state, options }) =>
    options.preserveFrontmatter
      ? h('pre', { key: state.key }, node.text)
      : null,

  [RuleType.codeBlock]: (node, { h, state }) => {
    const decodedLang = node.lang ? util.decodeEntityReferences(node.lang) : ''
    const codeProps = {
      ...(node.attrs || {}),
      class: decodedLang ? `language-${decodedLang} lang-${decodedLang}` : '',
    } as Record<string, unknown>
    return h('pre', { key: state.key }, h('code', codeProps, node.text))
  },

  [RuleType.codeInline]: (node, { h, state }) =>
    h('code', { key: state.key }, node.text),

  [RuleType.footnoteReference]: (node, { h, state, sanitize }) =>
    h(
      'a',
      { key: state.key, href: sanitize(node.target, 'a', 'href') },
      h('sup', {}, node.text)
    ),

  [RuleType.gfmTask]: (node, { h, state }) =>
    h('input', {
      key: state.key,
      checked: node.completed,
      readOnly: true,
      type: 'checkbox',
    }),

  [RuleType.heading]: (node, { h, output, state }) =>
    h(
      `h${node.level}`,
      { id: node.id, key: state.key },
      ...normalizeChildren(output(node.children, state))
    ),

  [RuleType.htmlBlock]: (node: MarkdownToJSX.HTMLNode, ctx) => {
    const { h, state, options, output, sanitize, slug, refs } = ctx

    if (util.tagfilterEnabled(options) && util.shouldFilterTag(node.tag)) {
      var filtered = util.getFilteredTagEmit(node)
      if (filtered.kind === 'literal') {
        return h('span', { key: state.key }, filtered.literal)
      }
      return h(
        'span',
        { key: state.key },
        ...util.assembleFilteredTagChildren(
          filtered.open,
          node.children ? output(node.children, state) : null,
          filtered.close
        )
      )
    }

    const rawOuter = node._rawOuter
    const isRawOuter = rawOuter !== undefined
    const rawSource = isRawOuter
      ? rawOuter
      : node._isClosingTag
        ? node._rawBody || ''
        : (node._rawBody || '') + (node._rawClose || '')
    if (rawSource && node._verbatim) {
      const tagLower = (node.tag as string).toLowerCase()
      const isType1Block = parse.isType1Block(tagLower)

      // Type 1 blocks (pre, script, style, textarea) always render verbatim.
      // The element wrapper below supplies its own closing tag, so the body
      // excludes _rawClose; the full outer span is used as-is only when no
      // closing tag was found (rare, matches the sniff below).
      if (isType1Block) {
        const type1Body = isRawOuter ? rawOuter : node._rawBody || ''
        const textContent = util.tagfilterEnabled(options)
          ? util.applyTagFilterToText(type1Body)
          : type1Body
        if (/<[a-z][^>]{0,100}>/i.test(rawSource)) {
          return h(node.tag, {
            key: state.key,
            ...node.attrs,
            innerHTML: textContent,
          })
        }
        return h(node.tag, { key: state.key, ...node.attrs }, textContent)
      }

      // Use already-parsed children if available instead of re-parsing rawSource
      const containsPreTags = /<\/?pre\b/i.test(rawSource)
      if (containsPreTags) {
        const innerHtml = util.tagfilterEnabled(options)
          ? util.applyTagFilterToText(rawSource)
          : rawSource
        return h(node.tag, {
          key: state.key,
          ...node.attrs,
          innerHTML: innerHtml,
        })
      }

      // Fallback to re-parsing rawSource if children not available (edge case)
      const parseOptions = parse.toParseOptions({
        slugify: options.slugify,
        sanitizer: sanitize,
        tagfilter: true,
      })
      const cleanedText = util.normalizeJsxReparseText(rawSource)

      const selfTagRegex = new RegExp(
        `^<${node.tag}(\\s[^>]*)?>(\\s*</${node.tag}>)?$`,
        'i'
      )
      const props = { key: state.key, ...node.attrs }
      if (selfTagRegex.test(cleanedText)) {
        return h(node.tag, props)
      }

      const astNodes = parse.parseMarkdown(
        cleanedText,
        { inline: false, refs, inHTML: false },
        parseOptions
      )
      // Clear verbatim on re-parsed nodes so a nested element does not absorb a
      // following text line into itself, then split at this element's own
      // closing tag so any trailing content renders as siblings (issue #881).
      util.stripVerbatim(astNodes)

      // _rawOuter means the parser already determined this span is the FULL
      // outer block, so emit the parsed nodes directly instead of nesting
      // them in another copy of the tag.
      if (isRawOuter) {
        return normalizeChildren(
          output(astNodes.flatMap(util.processVerbatimNode), state)
        )
      }

      const split = util.findOwnCloseInAST(astNodes, tagLower)
      if (split.found && split.afterClose.length > 0) {
        return [
          h(
            node.tag,
            props,
            ...normalizeChildren(
              output(split.beforeClose.flatMap(util.processVerbatimNode), state)
            )
          ),
          ...normalizeChildren(
            output(split.afterClose.flatMap(util.processVerbatimNode), state)
          ),
        ]
      }

      return h(
        node.tag,
        props,
        ...normalizeChildren(
          output(astNodes.flatMap(util.processVerbatimNode), state)
        )
      )
    }

    if (util.isVoidElement(node.tag)) {
      return h(node.tag, { key: state.key, ...node.attrs })
    }
    return h(
      node.tag,
      { key: state.key, ...node.attrs },
      ...normalizeChildren(node.children ? output(node.children, state) : null)
    )
  },

  [RuleType.htmlSelfClosing]: (
    node: MarkdownToJSX.HTMLSelfClosingNode,
    { h, state, options }
  ) => {
    if (util.tagfilterEnabled(options) && util.shouldFilterTag(node.tag)) {
      var filteredSc = util.getFilteredTagEmit(node)
      return h(
        'span',
        { key: state.key },
        filteredSc.kind === 'literal'
          ? filteredSc.literal
          : filteredSc.open + (filteredSc.close || '')
      )
    }
    return h(node.tag, { key: state.key, ...node.attrs })
  },

  [RuleType.image]: (node, { h, state, sanitize }) => {
    const src =
      node.target === null ? null : sanitize(node.target, 'img', 'src')
    return h('img', {
      key: state.key,
      alt: node.alt?.length > 0 ? node.alt : undefined,
      title: node.title || undefined,
      src: src || undefined,
    } as Record<string, unknown>)
  },

  [RuleType.link]: (node, { h, output, state, sanitize }) => {
    const props: Record<string, unknown> = { key: state.key }
    if (node.target != null) {
      // Re-sanitize at emit so direct astToJSX(dangerous) cannot skip the gate.
      const href = util.sanitizeAndEncodeUrlTarget(
        node.target,
        sanitize,
        'a',
        'href'
      )
      if (href != null) {
        props.href = href
      }
    }
    if (node.title) {
      props.title = node.title
    }
    return h('a', props, ...normalizeChildren(output(node.children, state)))
  },

  [RuleType.table]: (node: MarkdownToJSX.TableNode, { h, output, state }) => {
    const headerCells = node.header.map((content, i) =>
      h(
        'th',
        {
          key: i,
          style: node.align[i] === null ? {} : { textAlign: node.align[i] },
        },
        ...normalizeChildren(output(content, state))
      )
    )

    const rows = node.cells.map((row, i) =>
      h(
        'tr',
        { key: i },
        ...row.map((content, c) =>
          h(
            'td',
            {
              key: c,
              style: node.align[c] === null ? {} : { textAlign: node.align[c] },
            },
            ...normalizeChildren(output(content, state))
          )
        )
      )
    )

    var tableChildren = [h('thead', {}, h('tr', {}, ...headerCells))]
    if (node.cells.length > 0) {
      tableChildren.push(h('tbody', {}, ...rows))
    }
    return h('table', { key: state.key }, ...tableChildren)
  },

  [RuleType.text]: node => node.text,

  [RuleType.textFormatted]: (node, { h, output, state }) =>
    h(
      node.tag as string,
      { key: state.key },
      ...normalizeChildren(output(node.children, state))
    ),

  [RuleType.orderedList]: (
    node: MarkdownToJSX.OrderedListNode,
    { h, output, state }
  ) => {
    const items = node.items.map((item, i) =>
      h('li', { key: i }, ...normalizeChildren(output(item, state)))
    )
    const props: Record<string, unknown> = {
      key: state.key,
      start: node.start,
    }
    return h('ol', props, ...items)
  },

  [RuleType.unorderedList]: (
    node: MarkdownToJSX.UnorderedListNode,
    { h, output, state }
  ) => {
    const items = node.items.map((item, i) =>
      h('li', { key: i }, ...normalizeChildren(output(item, state)))
    )
    return h('ul', { key: state.key }, ...items)
  },

  [RuleType.paragraph]: (node, { h, output, state }) =>
    h(
      'p',
      { key: state.key },
      ...normalizeChildren(output(node.children, state))
    ),

  [RuleType.ref]: () => null, // Reference definitions should not be rendered
}

interface RenderContext {
  h: (
    tag: string | Component,
    props?: Record<string, unknown> & {
      class?: string
      id?: string
      key?: string | number
    },
    ...children: VueChild[]
  ) => VNode
  output: VueAstRender
  state: MarkdownToJSX.State
  sanitize: VueSanitizer
  slug: (input: string, defaultFn: (input: string) => string) => string
  refs: { [key: string]: { target: string; title: string | undefined } }
  options: VueOptions
}

function render(
  node: MarkdownToJSX.ASTNode,
  output: VueAstRender,
  state: MarkdownToJSX.State,
  h: (
    tag: string | Component,
    props?: Record<string, unknown> & {
      class?: string
      id?: string
      key?: string | number
    },
    ...children: VueChild[]
  ) => VNode,
  sanitize: VueSanitizer,
  slug: (input: string, defaultFn: (input: string) => string) => string,
  refs: { [key: string]: { target: string; title: string | undefined } },
  options: VueOptions
): VueChild[] | VueChild | null {
  const renderer = renderers[node.type]
  return renderer
    ? renderer(node, { h, output, state, sanitize, slug, refs, options })
    : null
}

const createRenderer = (
  userRender: VueOptions['renderRule'],
  h: (
    tag: string | Component,
    props?: Record<string, unknown> & {
      class?: string
      id?: string
      key?: string | number
    },
    ...children: VueChild[]
  ) => VNode,
  sanitize: VueSanitizer,
  slug: (input: string, defaultFn: (input: string) => string) => string,
  refs: { [key: string]: { target: string; title: string | undefined } },
  options: VueOptions
) => {
  const renderRule = (
    node: MarkdownToJSX.ASTNode,
    renderChildren: (
      children: MarkdownToJSX.ASTNode[]
    ) => (VNode | string)[] | VNode,
    state: MarkdownToJSX.State
  ) => {
    const defaultRender = () =>
      render(
        node,
        ast => renderChildren(Array.isArray(ast) ? ast : [ast]),
        state,
        h,
        sanitize,
        slug,
        refs,
        options
      )
    return userRender
      ? userRender(defaultRender, node, renderChildren, state)
      : defaultRender()
  }
  const handleStackOverflow = (ast: MarkdownToJSX.ASTNode[]) =>
    ast.map(node => ('text' in node ? node.text : '')) as VueChild[]
  const renderer = (
    ast: MarkdownToJSX.ASTNode[],
    state: MarkdownToJSX.State = {}
  ) => {
    const depth = (state.renderDepth || 0) + 1
    if (depth > 2500) {
      return handleStackOverflow(ast)
    }
    state.renderDepth = depth

    const oldKey = state.key
    const result: VueChild[] = []
    let lastWasString = false
    for (let i = 0; i < ast.length; i++) {
      state.key = i
      const nodeOut = renderRule(ast[i], renderer, state)
      const isString = typeof nodeOut === 'string'
      if (lastWasString && typeof nodeOut === 'string') {
        // Concatenate consecutive strings
        const last = result.at(-1)
        result[result.length - 1] =
          (typeof last === 'string' ? last : '') + nodeOut
      } else if (nodeOut != null) {
        if (Array.isArray(nodeOut)) {
          // Use loop instead of spread for better performance
          for (let j = 0; j < nodeOut.length; j++) {
            result.push(nodeOut[j])
          }
        } else {
          result.push(nodeOut)
        }
      }
      lastWasString = isString
    }
    state.key = oldKey
    state.renderDepth = depth - 1
    return result
  }
  return renderer
}

// A value is renderable as a Vue component if it is a function or an object
// with a render/setup definition
const isVueComponent = (value: unknown): value is Component =>
  typeof value === 'function' ||
  (typeof value === 'object' &&
    value != null &&
    ('render' in value || 'setup' in value))

const getTag = (
  tag: string | Component,
  overrides: VueOverrides | undefined
): string | Component => {
  if (typeof tag === 'function') {
    return tag
  }
  if (!overrides) {
    return tag
  }
  const tagStr = tag as string
  const override = util.get(overrides, tagStr, undefined)
  if (!override) {
    return tag
  }
  if (isVueComponent(override)) {
    return override
  }
  const component = util.get(overrides, `${tagStr}.component`, tagStr)
  if (isVueComponent(component)) {
    return component
  }
  return component as string
}

/**
 * Override configuration for HTML tags or custom components in Vue output
 */
export type VueOverride =
  | RequireAtLeastOne<{
      component: string | Component
      props: Record<string, unknown>
    }>
  | string
  | Component

/**
 * Map of HTML tags and custom components to their override configurations
 */
export interface VueOverrides {
  [tag: string]: VueOverride
}

/**
 * Vue compiler options
 */
export type VueOptions = Omit<
  MarkdownToJSX.Options,
  'createElement' | 'wrapperProps' | 'renderRule' | 'overrides'
> & {
  /** Custom createElement function (Vue's h function) */
  createElement?: typeof h
  /** Props for wrapper element */
  wrapperProps?: Record<string, unknown>
  /** Custom rendering function for AST rules */
  renderRule?: (
    next: () => VueChild[] | VueChild | null,
    node: MarkdownToJSX.ASTNode,
    renderChildren: (children: MarkdownToJSX.ASTNode[]) => VueChild[] | VNode,
    state: MarkdownToJSX.State
  ) => VueChild[] | VueChild | null
  /** Override configurations for HTML tags */
  overrides?: VueOverrides
}

/**
 * Convert AST nodes to Vue VNode elements
 *
 * @param ast - Array of AST nodes to render
 * @param options - Vue compiler options
 * @returns Vue VNode element(s)
 */
export function astToJSX(
  ast: MarkdownToJSX.ASTNode[],
  options?: VueOptions
): VNode | VNode[] | null {
  const opts = { ...(options || {}) }
  opts.overrides = opts.overrides || {}

  const slug = opts.slugify || util.slugify
  const sanitize = opts.sanitizer || util.sanitizer

  // Recursive compile function for HTML content
  const compileHtml = (input: string) =>
    compiler(input, { ...opts, wrapper: null })

  // JSX helper function - this is what @jsx pragma uses
  function hHelper(
    tag: string | Component,
    props?: Record<string, unknown> & {
      class?: string
      id?: string
      innerHTML?: string
    },
    ...children: VueChild[]
  ): VNode {
    const tagKey = typeof tag === 'string' ? tag : ''
    const hasOverrides =
      opts.overrides && Object.keys(opts.overrides).length > 0
    const overrideProps = hasOverrides
      ? (util.get(opts.overrides, `${tagKey}.props`, {}) as Record<
          string,
          unknown
        >)
      : null
    const vueProps = props ? (props as Record<string, unknown>) : null

    // Convert HTML content in props (early exit for non-HTML)
    if (vueProps) {
      for (const key in vueProps) {
        const value = vueProps[key]
        if (
          typeof value === 'string' &&
          value.length > 0 &&
          value[0] === '<' &&
          (parse.HTML_BLOCK_ELEMENT_START_R_ATTR.test(value) ||
            parse.UPPERCASE_TAG_R.test(value) ||
            parse.parseHTMLTag(value, 0))
        ) {
          const compiled = compileHtml(value.trim())
          // For innerHTML, take first element if array (matches original parser behavior)
          vueProps[key] =
            key === 'innerHTML' && Array.isArray(compiled)
              ? compiled[0]
              : compiled
        }
      }
    }

    const finalTag = hasOverrides ? getTag(tag, opts.overrides) : tag
    const vueClass = vueProps?.class as string
    const overrideClass = overrideProps?.class as string
    const mergedClass =
      vueClass && overrideClass
        ? `${vueClass} ${overrideClass}`
        : vueClass || overrideClass || undefined

    const isComponent = typeof finalTag !== 'string'

    if (!(hasOverrides || mergedClass || vueProps?.innerHTML)) {
      if (isComponent && children.length > 0) {
        return h(finalTag, vueProps || {}, { default: () => children })
      }
      return h(finalTag, vueProps || {}, ...children)
    }

    const finalProps: Record<string, unknown> = vueProps ? { ...vueProps } : {}
    if (overrideProps) {
      for (const key in overrideProps) {
        finalProps[key] = overrideProps[key]
      }
    }
    if (mergedClass) {
      finalProps.class = mergedClass
    }
    if (vueProps?.innerHTML && overrideProps?.innerHTML === undefined) {
      finalProps.innerHTML = vueProps.innerHTML
    }

    if (isComponent && children.length > 0) {
      return h(finalTag, finalProps, { default: () => children })
    }
    return h(finalTag, finalProps, ...children)
  }

  const createElement =
    opts.createElement ||
    ((
      tag: string | Component,
      props: Record<string, unknown>,
      ...children: VueChild[]
    ): VNode => {
      if (typeof tag !== 'string' && children.length > 0) {
        return h(tag, props || {}, { default: () => children })
      }
      return h(tag, props || {}, ...children)
    })

  const parseOptions = parse.toParseOptions(opts, opts.forceInline)

  const refs =
    ast[0] && ast[0].type === RuleType.refCollection
      ? (ast[0] as MarkdownToJSX.ReferenceCollectionNode).refs
      : (Object.create(null) as {
          [key: string]: { target: string; title: string | undefined }
        })

  const emitter = createRenderer(
    opts.renderRule,
    hHelper,
    sanitize,
    slug,
    refs,
    opts
  )
  const arr = emitter(ast, { inline: opts.forceInline, refs }) as (
    | VNode
    | string
  )[]

  const footnoteEntries = util.extractFootnoteEntries(refs)

  if (footnoteEntries.length > 0) {
    const footnoteNodes = footnoteEntries.map(def => {
      const identifierWithoutCaret = def.identifier.slice(1)
      const footnoteAstNodes = parse.parseMarkdown(
        def.footnote,
        { inline: true, refs },
        parseOptions
      )
      const footnoteContent = emitter(footnoteAstNodes, { inline: true, refs })
      const contentArray = normalizeChildren(footnoteContent)
      return hHelper(
        'div',
        { id: slug(identifierWithoutCaret, util.slugify) },
        `${identifierWithoutCaret}: `,
        ...contentArray
      )
    })
    arr.push(hHelper('footer', { key: 'footer' }, ...footnoteNodes))
  }

  if (opts.wrapper === null) {
    return arr.length === 0
      ? null
      : ((arr.length === 1 ? arr[0] : arr) as VNode | VNode[])
  }

  const wrapper = opts.wrapper || (opts.forceInline ? 'span' : 'div')
  if (arr.length > 1 || opts.forceWrapper) {
    return createElement(
      wrapper as string | Component,
      { ...opts.wrapperProps } as Record<string, unknown>,
      ...arr
    )
  }
  if (arr.length === 1) {
    return arr[0] as VNode
  }
  return null
}

/**
 * Compile markdown string to Vue VNode elements
 *
 * @param markdown - Markdown string to compile
 * @param options - Vue compiler options
 * @returns Vue VNode element(s)
 */
export function compiler(
  markdown = '',
  options: VueOptions = {}
): VNode | VNode[] | null {
  const opts = { ...(options || {}) }
  opts.overrides = opts.overrides || {}

  if (process.env.NODE_ENV !== 'production') {
    util.validateCompilerArgs(markdown, opts.overrides, 'VueComponent')
  }

  const _slug = opts.slugify || util.slugify
  const _sanitize = opts.sanitizer || util.sanitizer

  const inline =
    opts.forceInline ||
    !(opts.forceBlock || util.SHOULD_RENDER_AS_BLOCK_R.test(markdown))
  const parseOptions = parse.toParseOptions(opts, inline)

  const refs: { [key: string]: { target: string; title: string | undefined } } =
    Object.create(null)

  const processedInput = inline ? markdown : util.prepareBlockInput(markdown)

  const astNodes = parse.parseMarkdown(
    processedInput,
    { inline, refs },
    parseOptions
  )

  return astToJSX(astNodes, { ...opts, forceInline: inline } as VueOptions)
}

/**
 * Vue context provider for sharing compiler options across Markdown components
 *
 * @param options - Default compiler options to share
 * @param children - Vue children
 */
export const MarkdownProvider: Component<{
  options?: VueOptions
  children?: unknown
}> = props => {
  provide(MarkdownOptionsKey, props.options)
  return props.children as VNode
}

/**
 * A Vue component for easy markdown rendering. Feed the markdown content as a direct child
 * and the rest is taken care of automatically. Supports computed memoization for optimal performance.
 *
 * @param children - Markdown string content
 * @param options - Compiler options
 */
export const Markdown: Component<{
  children?: string | null
  options?: VueOptions
  [key: string]: unknown
}> = props => {
  const contextOptions = inject(MarkdownOptionsKey, undefined)

  const mergedOptions = computed(() => {
    var merged = { ...contextOptions, ...props.options }
    merged.overrides = {
      ...contextOptions?.overrides,
      ...props.options?.overrides,
    }
    return merged
  })

  const content =
    props.children === null || props.children === undefined
      ? ''
      : props.children

  const { options: _, children: __, ...restProps } = props

  const jsx = computed(() =>
    compiler(content, {
      ...mergedOptions.value,
      wrapperProps: {
        ...mergedOptions.value?.wrapperProps,
        ...restProps,
      },
    })
  )

  return jsx.value as VNode
}

export default Markdown
